import React, { Suspense, act, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { message, Spin } from 'antd';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import withAdminLayout from '../../layout/withAdminLayout';
import TrialExpiredModal from '../../components/TrialExpiredModal/ExpiredModal';
import OverView from './dashboard';

import Catalogue from './catalogue';
import Events from './event-management';
import Contacts from './contacts';
import ImportedContacts from './imported-contacts';
import GenAi from './gen-ai';
import UserManagement from './user-management';
import BroadCastCommunications from './broadcast-communications';
import ResponseTemplatesRoutes from './notification-templates';
import BotFlowManagement from './bot-flow-management';
import RequestCallback from './request-callback';
import Tickets from './tickets';
import Industries from './industries';
import WaForms from './wa-forms';
import GettingStarted from '../../pages/getting-started';
import { useNotification } from '../../context/notification.context';
import { socketClient } from '../../utility/SocketClient';
import FeatherIcon from 'feather-icons-react';
import { Modal } from 'antd';
import { useAuth } from '../../context/auth.context';
import BytesRoute from './bytes';
import { Redirect, useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import MySubscription from '../../pages/settings/my-subscription';
import TestWidget from '../../fab';
import Cookies, { set } from 'js-cookie';
import Feedbacks from './feedbacks';

const Settings = lazy(() => import('../../pages/settings'));
const LiveChatSupport = lazy(() => import('../../pages/live-chat-support'));
const Inbox = lazy(() => import('../../pages/inbox'));
const ForgotPassword = lazy(() => import('../../pages/authentication/ForgotPassword'));

const CreateWaForm = lazy(() => import('../../pages/wa-forms/create'));
const WebChatWidget = lazy(() => import('../../pages/web-chat-widget'));
const Calendar = lazy(() => import('../../container/calendar/Calendar'));

const MessageNotificationSoundPlayer = ({ notificationReceived }) => {
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        if (notificationReceived) {
            audioRef.current.play().catch((error) => {
                console.error('Error playing audio:', error);
            });
        } else {
            audioRef.current.pause();
        }
    }, [notificationReceived]);

    return <audio ref={audioRef} src="/assets/audio/message-notification.mp3" />;
};

const AlertSound = ({ play }) => {
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        console.log('play', play);
        if (play) {
            audioRef.current.play().catch((error) => {
                console.error('Error playing audio:', error);
            });
        } else {
            audioRef.current.pause();
        }
    }, [play]);

    return <audio ref={audioRef} src="/assets/audio/alert.mp3" />;
};

function Admin() {
    const { path } = useRouteMatch();
    const { notify } = useNotification();
    const { apiClient, updateUserStatus, currentUser, subscriptionPlan, setCurrentUser } = useAuth();
    const [notificationReceived, setNotificationReceived] = useState(false);
    const [alertSound, setAlertSound] = useState(false);
    const [ringtoneSound, setRingtoneSound] = useState(false);
    const [newCallBack, setNewCallBack] = useState(null);
    const [newChat, setNewChat] = useState(null);
    const history = useHistory();

    const activityPing = useCallback(async () => {
        try {
            await apiClient.get('/api/org-users/me/ping');
        } catch (error) {
            console.error('Error pinging user activity:', error);
        }
    }, [apiClient]);

    // const lastInteractionAt = useRef(0);
    // const lastFocusedAt = useRef(document.hasFocus() ? Date.now() : 0);

    useEffect(() => {
        // const ACTIVE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

        // const onUserInteraction = () => {
        //     lastInteractionAt.current = Date.now();
        //     if (currentUser?.status === 'offline') {
        //         updateUserStatus('online');
        //     }
        // };

        // const onFocusGained = () => {
        //     lastFocusedAt.current = Date.now();
        //     if (currentUser?.status === 'offline') {
        //         updateUserStatus('online');
        //     }
        // };

        // const onVisibilityChange = () => {
        //     if (document.visibilityState === 'visible') {
        //         lastFocusedAt.current = Date.now();
        //         if (currentUser?.status === 'offline') {
        //             updateUserStatus('online');
        //         }
        //     }
        // };

        // const events = ['click', 'keydown', 'scroll'];
        // events.forEach((e) => window.addEventListener(e, onUserInteraction, { passive: true }));
        // window.addEventListener('focus', onFocusGained);
        // document.addEventListener('visibilitychange', onVisibilityChange);

        // Immediate ping when tab becomes visible again
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                activityPing();
            }
        };
        const onFocus = () => activityPing();

        const onPageHide = () => {
            const token = Cookies.get('access_token');
            if (token) {
                navigator.sendBeacon(
                    `${process.env.REACT_APP_API_ENDPOINT}/api/org-users/me/update-status`,
                    new Blob([JSON.stringify({ status: 'offline' })], { type: 'application/json' }),
                );
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        window.addEventListener('pagehide', onPageHide);

        activityPing();

        const interval = setInterval(activityPing, 60000);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pagehide', onPageHide);
            // events.forEach((e) => window.removeEventListener(e, onUserInteraction));
            // window.removeEventListener('focus', onFocusGained);
            // document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [activityPing]);

    useEffect(() => {
        const onNotification = async (data) => {
            const parsedData = JSON.parse(data);
            let icon = null;
            if (parsedData.category === 'contact_messaged') {
                icon = <FeatherIcon icon="message-circle" size={16} />;
            }
            if (parsedData.category === 'request_callback') {
                icon = <FeatherIcon icon="phone-call" size={16} />;
                setNewCallBack(parsedData);
            }
            if (parsedData.category === 'general') {
                icon = <FeatherIcon icon="bell" size={16} />;
            }
            notify({
                message: parsedData.title,
                description: parsedData.message,
                type: parsedData.type,
                icon,
                btn: parsedData?.ctaLink && parsedData.ctaText && <a href={parsedData.ctaLink}>{parsedData.ctaText}</a>,
            });
            setNotificationReceived(true);
            setTimeout(() => setNotificationReceived(false), 300);
        };

        const onChatMessage = (data) => {
            const messageData = typeof data === 'string' ? JSON.parse(data) : data;
            if (messageData.newSessionInitiated) {
                setAlertSound(true);
                setNewChat(messageData);
            }
        };

        socketClient.on('org-user-notification', onNotification);
        socketClient.on('live_chat_message', onChatMessage);
        return () => {
            socketClient.removeListener('org-user-notification', onNotification);
            socketClient.removeListener('live_chat_message', onChatMessage);
        };
    }, [notify]);

    const location = useLocation();

    useEffect(() => {
        if (!subscriptionPlan && location.pathname !== '/admin/my-subscription') {
            history.replace('/admin/my-subscription');
        }
    }, [subscriptionPlan, location.pathname, history]);

    return (
        <>
            <MessageNotificationSoundPlayer notificationReceived={notificationReceived} />
            <AlertSound play={alertSound} />

            <Modal
                open={newCallBack !== null}
                cancelText="Close"
                okText="View"
                onCancel={() => {
                    setNewCallBack(null);
                }}
                onOk={() => {
                    setNewCallBack(null);
                    history.push(`${path}/request-callback/request-pool`);
                }}
                title={newCallBack?.title}
            >
                {newCallBack?.message}
            </Modal>

            <Modal
                open={newChat !== null}
                cancelText="Close"
                okText="View"
                onCancel={() => {
                    setNewChat(null);
                }}
                onOk={() => {
                    setNewChat(null);
                    history.push(`${path}/inbox`);
                }}
                title="New Live Chat Session"
            >
                <p>A new chat session has been initiated with {newChat?.contact?.name}</p>
            </Modal>
            <TestWidget />
            <Switch>
                <Suspense
                    fallback={
                        <div className="spin">
                            <Spin />
                        </div>
                    }
                >
                    {!subscriptionPlan && <TrialExpiredModal />}

                    {subscriptionPlan ? (
                        <>
                            <Route path={path} component={OverView} />
                            <Route path={`${path}/overview`} component={OverView} />
                            {/* ========== Content Management ======== */}

                            <Route path={`${path}/catalogue`} component={Catalogue} />
                            <Route path={`${path}/imported-contacts`} component={ImportedContacts} />

                            <Route path={`${path}/industries`} component={Industries} />
                            <Route path={`${path}/wa-forms`} component={WaForms} />
                            <Route path={`${path}/bot-flow`} component={BotFlowManagement} />

                            <Route path={`${path}/event-management`} component={Events} />

                            <Route path={`${path}/contacts`} component={Contacts} />
                            <Route path={`${path}/feedbacks`} component={Feedbacks} />

                            <Route path={`${path}/gen-ai`} component={GenAi} />

                            <Route path={`${path}/live-chat-support`} component={LiveChatSupport} />

                            <Route path={`${path}/inbox`} component={Inbox} />

                            <Route path={`${path}/communications`} component={BroadCastCommunications} />

                            <Route path={`${path}/request-callback`} component={RequestCallback} />

                            <Route path={`${path}/tickets`} component={Tickets} />

                            <Route path={`${path}/notification-templates`} component={ResponseTemplatesRoutes} />

                            <Route path={`${path}/users-management`} component={UserManagement} />

                            <Route path={`${path}/settings`} component={Settings} />

                            <Route path={`${path}/getting-started`} component={GettingStarted} />

                            <Route path={`${path}/forgot-password`} component={ForgotPassword} />

                            <Route path={`${path}/create-wa-form`} component={CreateWaForm} />

                            <Route path={`${path}/web-chat-widget`} component={WebChatWidget} />

                            <Route path={`${path}/bytes`} component={BytesRoute} />

                            <Route path={`${path}/calendar`} component={Calendar} />

                            <Route path="/admin/my-subscription" component={MySubscription} />
                        </>
                    ) : (
                        <Route path="/admin/my-subscription" component={MySubscription} />
                    )}
                </Suspense>
            </Switch>
        </>
    );
}

export default withAdminLayout(Admin);
